<template>
  <div class="legal-page">
    <!-- Monogram Background Layer -->
    <div class="monogram-wrapper">
      <div class="monogram-background"></div>
    </div>

    <!-- Header Hero Section -->
    <section class="legal-hero">
      <div class="container">
        <div class="hero-content">
          <span class="hero-badge">{{ t('legal.privacy.badge') }}</span>
          <h1 class="hero-title">{{ t('legal.privacy.title') }}</h1>
          <div class="hero-divider"></div>
          <p class="last-update">{{ t('legal.last_update') }}</p>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="legal-body">
      <div class="container">
        <div class="legal-layout">
          <!-- Sidebar Navigation -->
          <aside class="legal-sidebar desktop-only">
            <div class="sidebar-sticky">
              <nav class="side-nav">
                 <a 
                   v-for="(section, index) in privacySections" 
                   :key="index"
                   :href="'#section-' + index"
                   class="nav-anchor"
                 >
                   <span class="nav-num">{{ (index + 1).toString().padStart(2, '0') }}</span>
                   <span class="nav-text">{{ section.h }}</span>
                 </a>
              </nav>
            </div>
          </aside>

          <!-- Main Legal Content -->
          <div class="legal-main">
             <div 
               v-for="(section, index) in privacySections" 
               :key="index" 
               :id="'section-' + index"
               class="legal-section-card"
             >
              <div class="card-inner">
                <div class="section-header">
                  <span class="section-index">// {{ (index + 1).toString().padStart(2, '0') }}</span>
                  <h2 v-if="section.h">{{ section.h }}</h2>
                </div>
                
                <div class="section-content">
                  <p v-if="section.p" v-html="section.p"></p>
                  <div v-if="section.list" class="list-container">
                    <ul class="premium-list">
                      <li v-for="(item, i) in section.list" :key="i" v-html="item"></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

             <!-- Bottom Disclaimer -->
             <div class="legal-footer-note">
               <p>{{ privacyDisclaimer }}</p>
             </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useLanguage } from '@/composables/useLanguage'
import { computed } from 'vue'

const { t } = useLanguage()

const infoEmail = import.meta.env.VITE_INFO_EMAIL

const privacySections = computed(() => {
  const sections = t('legal.privacy.sections')
  return sections.map(section => ({
    ...section,
    p: section.p?.replace?.('{email}', infoEmail) || section.p,
    list: section.list?.map(item => String(item).replace('{email}', infoEmail)) || section.list
  }))
})

const privacyDisclaimer = computed(() => {
  return t('legal.privacy.disclaimer').replace('{email}', infoEmail)
})
</script>

<style scoped>
.legal-page {
  background-color: #ffffff;
  color: #000;
  min-height: 100vh;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow-x: hidden;
}

/* Texture Monogramma Billy B (Coerente con Countdown) */
.monogram-wrapper {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.monogram-background {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: url("@/assets/images/logo.png");
  background-size: 120px auto;
  background-repeat: repeat;
  opacity: 0.22;
  transform: rotate(-15deg);
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 25px;
  position: relative;
  z-index: 1;
}

/* Hero Section - Extremely Compact */
.legal-hero {
  padding: 40px 0 25px;
  background-color: #000;
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: block;
  font-size: 9px;
  font-weight: 800;
  color: var(--bright-teal);
  letter-spacing: 3px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.hero-title {
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  margin-bottom: 12px;
  line-height: 1.1;
  color: #fff;
}

.hero-divider {
  width: 30px;
  height: 2px;
  background-color: var(--bright-teal);
  margin: 0 auto 12px;
}

.last-update {
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  font-style: italic;
}

/* Body Layout */
.legal-body {
  padding: 60px 0 100px;
  position: relative;
  z-index: 1;
}

.legal-layout {
  display: flex;
  gap: 50px;
}

@media (max-width: 992px) {
  .legal-layout {
    flex-direction: column;
    gap: 25px;
  }
  .legal-sidebar {
    flex: 0 0 auto;
    width: 100%;
  }
  .sidebar-sticky {
    position: static;
  }
}

/* Sidebar - Compact Box */
.legal-sidebar {
  flex: 0 0 240px;
  position: relative;
  z-index: 2;
}

.sidebar-sticky {
  position: sticky;
  top: 110px;
  background-color: #fff;
  padding: 25px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nav-anchor {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 15px;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.nav-num {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 800;
  color: var(--dark-teal);
  margin-top: 2px;
}

.nav-text {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-anchor:hover .nav-text {
  color: var(--dark-teal);
}

.nav-anchor:hover {
  border-left-color: var(--dark-teal);
  background: rgba(0, 0, 0, 0.02);
}

/* Main Content - Black Blocks */
.legal-main {
  flex: 1;
}

.legal-section-card {
  margin-bottom: 30px;
  scroll-margin-top: 130px;
}

.card-inner {
  background-color: #000;
  padding: 40px;
  border-radius: 4px;
  border-left: 4px solid var(--bright-teal);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.section-header {
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
}

.section-index {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 800;
  color: var(--bright-teal);
  margin-bottom: 5px;
  letter-spacing: 2px;
}

.section-header h2 {
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  color: #fff;
}

.section-content p {
  font-size: 15px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 15px;
  text-align: justify;
}

.list-container {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.premium-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.premium-list li {
  position: relative;
  padding-left: 25px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
}

.premium-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 10px;
  height: 1px;
  background-color: var(--bright-teal);
}

.legal-footer-note {
  margin-top: 40px;
  padding: 30px;
  background: #f9f9f9;
  border: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;
}

.legal-footer-note p {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
}

/* Mobile Adjustments */
@media (max-width: 1024px) {
  .legal-layout {
    gap: 30px;
  }
  .card-inner {
    padding: 30px 20px;
  }
}

@media (max-width: 768px) {
  .legal-hero {
    padding: 20px 0 15px;
  }
  .hero-title {
    font-size: 18px;
    padding: 0 5px;
    line-height: 1.3;
  }
  .last-update {
    font-size: 10px;
  }
  .legal-body {
    padding: 20px 0 40px;
  }
  .legal-layout {
    flex-direction: column;
    gap: 15px;
  }
  .legal-sidebar {
    display: none !important;
  }
  .card-inner {
    padding: 20px 15px;
  }
  .legal-section-card {
    margin-bottom: 15px;
  }
  .section-header h2 {
    font-size: 14px;
  }
  .section-content p {
    font-size: 13px;
    line-height: 1.5;
    text-align: left;
  }
  .premium-list li {
    font-size: 12.5px;
    padding-left: 15px;
  }
  .monogram-background {
    background-size: 60px auto;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 10px;
  }
  .hero-title {
    font-size: 16px;
  }
  .card-inner {
    padding: 15px 10px;
  }
  .badge-premium {
    font-size: 9px;
  }
}
</style>
